import dayjs from 'dayjs';

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ChartHeader from 'components/charts/ChartHeader';
import DataCards from 'components/charts/DataCards';
import DataListingCard from 'components/charts/DataListingCard';
import DateFilter from 'components/field/DateFilter';

import useFetchAndParseOnView from 'hooks/useFetchAndParseOnView';

import { routeMappingForBranch } from 'pages/Shipments/utils/shipmentStatusMapping';

import { BRANCH_USERS } from 'services/consts';

const BRDashboard = () => (
	<div>
		<div className="row row-cols-1 row-cols-lg-1 mt-3">
			<Overview />
		</div>
		<div className="row row-cols-1 row-cols-lg-1 mt-3">
			<BranchPerformance />
		</div>
		<div className="row row-cols-1 row-cols-lg-3 mt-3">
			<DataListingCard
				defaultDay="last30Days"
				title="awaitingPickup"
				fetchURL={BRANCH_USERS.GET_BRANCH_WAITING_PICKUP_BYDC}
			/>
		</div>
	</div>
);

const Overview = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(1, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const navigate = useNavigate();

	const handleButtonClick = (label) => {
		navigate(routeMappingForBranch[label], {
			state: {
				defaultFilters: [
					{
						column: {
							Header: 'orderDate',
							accessor: 'createdDate',
							filterType: 'date'
						},
						action: 'between',
						...(body && {
							value: {
								start: body.from,
								end: body.to
							}
						})
					}
				]
			}
		});
	};

	const cardsConfig = [
		{
			accessor: 'pending',
			label: 'pending',
			customIcon: 'fa-regular-loader-circle-bolt',
			backgroundColor: 'var(--fa-regular-loader-circle-bolt-bgColor)',
			color: 'var(--fa-regular-loader-circle-bolt-color)',
			onButtonClick: () => handleButtonClick('pending')
		},
		{
			accessor: 'waitingDriver',
			label: 'waitingDriver',
			customIcon: 'fa-regular-box-circle-arrow-up',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-up-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-up-color)',
			onButtonClick: () => handleButtonClick('waitingDriver')
		},
		{
			accessor: 'shipmentInProgress',
			label: 'shipmentInProgress',
			customIcon: 'fa-regular-box-circle-arrow-up',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-up-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-up-color)',
			onButtonClick: () => handleButtonClick('shipmentInProgress')
		},
		{
			accessor: 'returned',
			label: 'returned',
			customIcon: 'fa-regular-box-circle-arrow-left',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-left-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-left-color)',
			onButtonClick: () => handleButtonClick('returned')
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: BRANCH_USERS.GET_BRANCH_TODAYS_ACTIVITY,
		body
	});

	return (
		<div ref={ref}>
			<ChartHeader title="overview" filter={<DateFilter defaultDay={'today'} setValue={setFilter} />} />
			<DataCards data={data} loading={loading} cardsConfig={cardsConfig} />
		</div>
	);
};

const BranchPerformance = () => {
	const [filter, setFilter] = useState({
		start: dayjs().subtract(30, 'day').endOf('day').toISOString(),
		end: dayjs().endOf('day').toISOString()
	});

	const body = useMemo(() => ({ from: filter.start, to: filter.end }), [filter]);

	const cardsConfig = [
		{
			accessor: 'avgDeliveryTime',
			label: 'avgDeliveryTime',
			customIcon: 'fa-regular-wave-pulse-circle-arrow-left',
			tooltip: 'dashboard.avgDeliveryTime.description',
			backgroundColor: 'var(--fa-regular-wave-pulse-circle-arrow-left-bgColor)',
			color: 'var(--fa-regular-wave-pulse-circle-arrow-left-color)'
		},
		{
			accessor: 'avgPreperationDelay',
			label: 'avgPreperationDelay',
			customIcon: 'fa-regular-box-circle-check',
			tooltip: 'dashboard.avgPreperationDelay.description',
			backgroundColor: 'var(--fa-regular-box-circle-check-bgColor)',
			color: 'var(--fa-regular-box-circle-check-color)'
		},
		{
			accessor: 'avgPreperationTime',
			label: 'avgPreperationTime',
			customIcon: 'fa-regular-box-circle-check',
			tooltip: 'dashboard.avgPreperationTime.description',
			backgroundColor: 'var(--fa-regular-box-circle-check-bgColor)',
			color: 'var(--fa-regular-box-circle-check-color)'
		},
		{
			accessor: 'delayRate',
			label: 'delayRate',
			tooltip: 'dashboard.delayRate.description',
			customIcon: 'fa-regular-boxes-stacked-circle-check',
			backgroundColor: 'var(--fa-regular-boxes-stacked-circle-check-bgColor)',
			color: 'var(--fa-regular-boxes-stacked-circle-check-color)'
		},
		{
			accessor: 'noOfDelivery',
			label: 'noOfDelivery',
			customIcon: 'fa-regular-box-circle-arrow-left',
			tooltip: 'dashboard.noOfDelivery.description',
			backgroundColor: 'var(--fa-regular-box-circle-arrow-left-bgColor)',
			color: 'var(--fa-regular-box-circle-arrow-left-color)'
		},
		{
			accessor: 'totalSales',
			label: 'totalSales',
			tooltip: 'dashboard.totalSales.description',
			customIcon: 'fa-regular-box-circle-plus',
			backgroundColor: 'var(--ffa-regular-box-circle-plus-bgColor)',
			color: 'var(--fa-regular-box-circle-plus-color)'
		}
	];

	const { ref, data, loading } = useFetchAndParseOnView({
		url: BRANCH_USERS.GET_BRANCH_PERFORMANCE,
		body
	});

	return (
		<div ref={ref}>
			<ChartHeader
				title={'branchPerformance'}
				filter={<DateFilter defaultDay={'last30Days'} setValue={setFilter} />}
			/>
			<DataCards data={data} loading={loading} cardsConfig={cardsConfig} />
		</div>
	);
};

export default BRDashboard;
